import { useState, useEffect } from 'react';
import { useCaptureDetailsApi } from './useCaptureDetailsApi';

export const useOtpLogin = (initialEmail: string = '') => {
  const urlParams = new URLSearchParams(window.location.search);
  const authStateKey = urlParams.get('auth_state_key') || '';

  const { generateOtp, validateOtp } = useCaptureDetailsApi();

  const [email, setEmail] = useState<string>(initialEmail);
  const [isOtpSent, setIsOtpSent] = useState<boolean>(urlParams.get('step') === 'OTP');
  const [otp, setOtp] = useState<string>('');
  const [timer, setTimer] = useState<number>(60);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [isLockedOut, setIsLockedOut] = useState<boolean>(false);
  const [attempts, setAttempts] = useState<number>(0);

  // Countdown timer for Resend OTP
  useEffect(() => {
    if (!isOtpSent || canResend) return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOtpSent, canResend]);

  const handleChange = (value: string) => {
    const cleanValue = value.replace(/\D/g, '').slice(0, 6);
    setOtp(cleanValue);
    setError(null);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = event.clipboardData.getData('text').trim().replace(/\D/g, '').slice(0, 6);
    if (pasteData.length === 6) {
      event.preventDefault();
      setOtp(pasteData);
      setError(null);
    }
  };

  const handleSendOtp = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please provide a valid email address.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await generateOtp(email, authStateKey);
      setIsOtpSent(true);
      setTimer(60);
      setCanResend(false);
      setOtp('');
      setAttempts(0);
      setIsLockedOut(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send OTP. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || isLockedOut) return;

    setIsResending(true);
    setError(null);

    try {
      await generateOtp(email, authStateKey);
      setTimer(60);
      setCanResend(false);
      setOtp('');
      setAttempts(0);
      setIsLockedOut(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to resend OTP. Please try again.';
      setError(message);
    } finally {
      setIsResending(false);
    }
  };

  const handleContinue = async () => {
    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit security code.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await validateOtp(email, otp, authStateKey);
      if (data?.redirectUri) {
        window.location.replace(data.redirectUri);
        return;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid security code.';
      setError(message);
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= 3) {
        setIsLockedOut(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsOtpSent(false);
    setOtp('');
    setError(null);
    setAttempts(0);
    setIsLockedOut(false);
  };

  const canContinue = otp.length === 6 && !isLockedOut && !isLoading;

  return {
    email,
    setEmail,
    isOtpSent,
    otp,
    timer,
    canResend,
    error,
    setError,
    isLoading,
    isResending,
    isLockedOut,
    handleChange,
    handlePaste,
    handleContinue,
    handleResend,
    handleCancel,
    handleSendOtp,
    canContinue,
  };
};
